import { Link } from 'react-router-dom';
import { useMemo, type MouseEvent } from 'react';
import { Tooltip, Typography } from 'antd';
import { ClockCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { evimoTableActionsColumn } from '~/app/components/common/EvimoTable/evimoTableColumnHelpers';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import type { ColumnsType } from 'antd/es/table';
import styles from '~/app/components/asset-overview/AssetOverviewTable.module.css';
import {
  getConnectivityBadge,
  getLastSeenCell,
} from '~/app/components/asset-overview/utils';
import type { AssetListItem } from '~/app/services/asset/asset.service';
import {
  formatRelativeLastSeen,
  resolveDateFnsLocale,
} from '~/app/utils/format-relative-last-seen';
import { formatDateTime } from '~/app/utils/format-date-time';
import type { TFunction } from 'i18next';

const { Text } = Typography;

const CONNECTIVITY_LABELS = {
  online: 'Online',
  offline: 'Offline',
  unknown: 'Unknown',
} as const;

function stopRowNavigation(event: MouseEvent): void {
  event.stopPropagation();
}

function formatEarningByDay(value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

type UseAssetOverviewTableColumnsOptions = {
  t: TFunction<'assets'>;
  language: string;
  onDelete?: (asset: AssetListItem) => void;
};

export function useAssetOverviewTableColumns({
  t,
  language,
  onDelete,
}: UseAssetOverviewTableColumnsOptions): ColumnsType<AssetListItem> {
  return useMemo(
    () => [
      {
        title: t('overview.table.assetName', 'Asset name'),
        dataIndex: 'assetName',
        key: 'assetName',
        minWidth: 200,
        render: (_value: string, row: AssetListItem) => (
          <Link
            to={`/assets/${row.id}`}
            onClick={(event) => event.stopPropagation()}
          >
            {row.assetName}
          </Link>
        ),
      },
      {
        title: t('overview.table.connectivity', 'Connectivity'),
        dataIndex: 'connectivityStatus',
        key: 'connectivityStatus',
        minWidth: 120,
        render: (
          value: AssetListItem['connectivityStatus'],
          row: AssetListItem,
        ) => {
          const badge = getConnectivityBadge(value, row.lastSeenAt);
          if (!badge) {
            return t('overview.table.notMonitored', '-');
          }
          const lastSeen = badge.lastSeenAt
            ? t('overview.table.lastSeenTooltip', 'Last seen: {{date}}', {
                date: formatDateTime(badge.lastSeenAt),
              })
            : t('overview.table.neverSeen', 'Never seen');
          const tooltip = badge.explainsUnknown
            ? `${t(
                'overview.table.unknownTooltip',
                'The platform cannot measure connectivity right now',
              )}. ${lastSeen}`
            : lastSeen;
          return (
            <Tooltip title={tooltip} trigger={['hover', 'focus', 'click']}>
              <span
                className={`ds-pill ds-pill-${badge.tone}`}
                tabIndex={0}
                onClick={stopRowNavigation}
              >
                {t(
                  `overview.table.${badge.label}`,
                  CONNECTIVITY_LABELS[badge.label],
                )}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: t('overview.table.lastSeen', 'Last seen'),
        dataIndex: 'lastSeenAt',
        key: 'lastSeenAt',
        minWidth: 150,
        render: (value: AssetListItem['lastSeenAt'], row: AssetListItem) => {
          const cell = getLastSeenCell(row.connectivityStatus, value);
          if (cell.kind === 'notMonitored') {
            return t('overview.table.notMonitored', '-');
          }
          if (cell.kind === 'neverSeen') {
            return (
              <span className={styles.lastSeenCell}>
                <ClockCircleOutlined className={styles.lastSeenIcon} />
                <span className={styles.lastSeenText}>
                  {t('overview.table.neverSeen', 'Never seen')}
                </span>
              </span>
            );
          }
          const { label, tooltip } = formatRelativeLastSeen(
            cell.value,
            resolveDateFnsLocale(language),
          );
          const content = (
            <span className={styles.lastSeenCell}>
              <ClockCircleOutlined className={styles.lastSeenIcon} />
              <span className={styles.lastSeenText}>{label}</span>
            </span>
          );
          if (!tooltip) {
            return content;
          }
          return <Tooltip title={tooltip}>{content}</Tooltip>;
        },
      },
      {
        title: t('overview.table.serialNumber', 'Serial number'),
        dataIndex: 'serialNumber',
        key: 'serialNumber',
        minWidth: 180,
        render: (value: AssetListItem['serialNumber']) => {
          const serialNumber = value?.trim();
          if (!serialNumber) {
            return t('overview.table.noSerialNumber', '—');
          }

          return (
            <Text
              copyable={{ text: serialNumber }}
              className={styles.serialNumberText}
              onClick={stopRowNavigation}
            >
              {serialNumber}
            </Text>
          );
        },
      },
      {
        title: t('overview.table.incidentsCount', 'Incidents'),
        dataIndex: 'incidentsCount',
        key: 'incidentsCount',
        minWidth: 96,
        align: 'right' as const,
      },
      {
        title: t('overview.table.assetModel', 'Asset model'),
        dataIndex: 'assetModel',
        key: 'assetModel',
        minWidth: 140,
        render: (value: AssetListItem['assetModel']) => value?.name ?? '-',
      },
      {
        title: t('overview.table.landlord', 'Landlord'),
        dataIndex: 'landlord',
        key: 'landlord',
        minWidth: 160,
        ellipsis: true,
        render: (value: AssetListItem['landlord']) =>
          value?.name ?? t('overview.table.noLandlord', '—'),
      },
      {
        title: t('overview.table.preventivePlan', 'PM plan'),
        dataIndex: 'preventivePlan',
        key: 'preventivePlan',
        minWidth: 180,
        ellipsis: true,
        render: (value: AssetListItem['preventivePlan']) => {
          if (!value) {
            return t('overview.table.noPreventivePlan', '—');
          }
          const fullLabel = `${value.code} — ${value.name}`;
          return (
            <Tooltip title={fullLabel}>
              <Link
                to={`/maintenance/preventive/${value.id}`}
                onClick={(event) => event.stopPropagation()}
              >
                {value.name}
              </Link>
            </Tooltip>
          );
        },
      },
      {
        title: t(
          'overview.table.expectedDailyEarning',
          'Expected earning (€/day)',
        ),
        dataIndex: 'expectedDailyEarning',
        key: 'expectedDailyEarning',
        minWidth: 170,
        align: 'right' as const,
        render: (value: AssetListItem['expectedDailyEarning']) =>
          formatEarningByDay(value),
      },
      {
        title: (
          <Tooltip
            title={t(
              'overview.table.calculatedDailyEarningHint',
              'Daily revenue estimated by the AI engine from asset performance and active contracts.',
            )}
          >
            <span>
              {t(
                'overview.table.calculatedDailyEarning',
                'AI-estimated earning (€/day)',
              )}
            </span>
          </Tooltip>
        ),
        dataIndex: 'calculatedDailyEarning',
        key: 'calculatedDailyEarning',
        minWidth: 190,
        align: 'right' as const,
        render: (value: AssetListItem['calculatedDailyEarning']) =>
          formatEarningByDay(value),
      },
      {
        title: t('overview.table.assetType', 'Asset type'),
        dataIndex: 'assetType',
        key: 'assetType',
        minWidth: 140,
        render: (_value: AssetListItem['assetType'], row: AssetListItem) =>
          row.assetType.name,
      },
      {
        title: t('overview.table.installedAt', 'Installed at'),
        dataIndex: 'installedAt',
        key: 'installedAt',
        minWidth: 165,
        render: (value: AssetListItem['installedAt']) => formatDateTime(value),
      },
      {
        title: t('overview.table.createdAt', 'Created at'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        minWidth: 165,
        responsive: ['xl'] as const,
        render: (value: AssetListItem['createdAt']) => formatDateTime(value),
      },
      {
        title: t('overview.table.updatedAt', 'Updated at'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        minWidth: 165,
        responsive: ['xl'] as const,
        render: (value: AssetListItem['updatedAt']) => formatDateTime(value),
      },
      ...(onDelete
        ? [
            {
              title: t('overview.table.actions', 'Actions'),
              key: 'actions',
              ...evimoTableActionsColumn,
              render: (_: unknown, row: AssetListItem) => (
                <TableRowActionsMenu
                  items={[
                    {
                      key: 'delete',
                      label: t('delete.button'),
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => onDelete(row),
                    },
                  ]}
                />
              ),
            },
          ]
        : []),
    ],
    [t, language, onDelete],
  );
}
